import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Button, 
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment
} from '@mui/material';
import VenueCard from './VenueCard';
import { Box } from '@mui/material';
import { toast } from 'react-toastify';
import { req } from '../utils';

const LeadsList = ({ venues, onNewLeads }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchType, setSearchType] = useState('location');
  const [address, setAddress] = useState('');
  const [radius, setRadius] = useState(5);
  const [loading, setLoading] = useState(false);
  const [sortedVenues, setSortedVenues] = useState([]);
  const [userLocation, setUserLocation] = useState(null);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in km
  };

  useEffect(() => {
    const sortVenues = async () => {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });
        
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });

        const venuesWithDistance = venues.map(venue => {
          const data = JSON.parse(venue.data);
          const distance = calculateDistance(
            position.coords.latitude,
            position.coords.longitude,
            data.geometry.location.lat,
            data.geometry.location.lng
          );
          return { ...venue, distance };
        });

        const sorted = venuesWithDistance.sort((a, b) => a.distance - b.distance);
        setSortedVenues(sorted);
      } catch (error) {
        console.error('Error getting location:', error);
        setSortedVenues(venues); // Fallback to unsorted venues
      }
    };

    if (venues && venues.length > 0) {
      sortVenues();
    } else {
      setSortedVenues([]);
    }
  }, [venues]);

  const handleGetNewLeads = async () => {
    setLoading(true);
    try {
      let params = {
        radius: radius * 1000 // Convert km to meters
      };

      if (searchType === 'location') {
        // Get current location
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });

        params.lat = position.coords.latitude;
        params.lng = position.coords.longitude;
      } else {
        params.address = address;
      }

      const response = await req('POST', 'fetch_new_leads', params);
      
      if (Array.isArray(response) && response.length > 0) {
        onNewLeads(response);
        setModalOpen(false);
        toast.success(`Successfully fetched ${response.length} new leads`);
      } else {
        toast.info('No new leads found in this area');
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch new leads. Please check your location settings.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          Nearby Venues
        </Typography>
        <Button 
          variant="contained" 
          color="persiangreen"
          onClick={() => setModalOpen(true)}
        >
          Get new leads
        </Button>
      </Box>

      <Grid container spacing={3}>
        {Array.isArray(sortedVenues) && sortedVenues.length > 0 ? (
          sortedVenues.map((venue) => (
            <Grid item key={venue.id} xs={12} md={6}>
              <VenueCard 
                venue={{
                  ...venue,
                  // Parse the JSON strings into objects
                  photos: JSON.parse(venue.photos),
                  data: JSON.parse(venue.data),
                  types: JSON.parse(venue.types)
                }} 
              />
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Typography variant="body1" align="center">
              No venues found
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* New Leads Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Get New Leads</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          >
            <FormControlLabel 
              value="location" 
              control={<Radio />} 
              label="Use my current location" 
            />
            <FormControlLabel 
              value="address" 
              control={<Radio />} 
              label="Search by address" 
            />
          </RadioGroup>

          {searchType === 'address' && (
            <TextField
              fullWidth
              label="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              margin="normal"
            />
          )}

          <TextField
            fullWidth
            label="Search Radius"
            type="number"
            value={radius}
            onChange={(e) => setRadius(Math.max(1, Math.min(50, Number(e.target.value))))}
            margin="normal"
            InputProps={{
              endAdornment: <InputAdornment position="end">km</InputAdornment>,
            }}
            helperText="Enter a value between 1 and 50 kilometers"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleGetNewLeads} 
            variant="contained"
            disabled={loading || (searchType === 'address' && !address.trim())}
          >
            {loading ? 'Loading...' : 'Search'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LeadsList;
